import {
  FEATURE_FLAG_KEYS,
  type FeatureFlagSnapshot,
} from "../services/feature-flags";
import {
  isJsonObject,
  isRecord,
  isStringRecord,
  validationFailure,
  validationSuccess,
  type JsonObject,
  type JsonValue,
  type ValidationResult,
} from "./validation";

export const APPLICATION_SCHEMA_VERSION = 1 as const;

export const STORED_RECORD_TYPES = [
  "event",
  "settings",
  "calibration",
  "fills",
  "cue-positions",
  "lexicon-choices",
  "notepad",
  "feature-flags",
  "questions",
] as const;

export type StoredRecordType = (typeof STORED_RECORD_TYPES)[number];

export interface EventData {
  readonly action: string;
  readonly message: string;
  readonly module: string;
  readonly stack: string | null;
}

export interface SettingsData {
  readonly values: JsonObject;
}

export interface CalibrationData {
  readonly voiceId: string;
  readonly wordsPerMinute: number;
}

export interface FileValuesData {
  readonly fileHash: string;
  readonly values: Readonly<Record<string, string>>;
}

export interface NotepadData {
  readonly fileHash: string;
  readonly text: string;
}

/**
 * R1 Step 14. The only confirmatory-questions signal worth keeping is which
 * questions the trainee revealed without answering. No score, no grade.
 */
export interface QuestionsData {
  readonly fileHash: string;
  readonly revealedWithoutAnswering: readonly {
    readonly nodeId: string;
    readonly questionId: string;
  }[];
}

export interface FeatureFlagsData {
  readonly values: FeatureFlagSnapshot;
}

export type TracePoint = readonly [number, number];

/** Ruling 25, 18 September 2026. The sidecar's own account of a grid. */
export const TRACE_GRID_STATES = [
  "confirmed",
  "low-confidence",
  "unread",
] as const;

export type TraceGridState = (typeof TRACE_GRID_STATES)[number];

/**
 * Sidecar schema version. Deliberately its own number rather than the
 * application schema version: the sidecar is authored by hand, outside this
 * repository, and moves on its own schedule.
 */
export const TRACE_SIDECAR_SCHEMA_VERSION = 4 as const;

export interface TraceFeature {
  /**
   * The callsign the completed order set gives this mark, verbatim. Ruling
   * 35, 18 September 2026.
   *
   * It is what lets a blank owned by one callsign find the one marker of
   * five that belongs to it. **Never inferred** — not from geometry, not from
   * a lane or a boundary, not from numbering. If the order set does not state
   * it, it is not stated and the field is absent.
   */
  readonly assignedTo?: string;
  /**
   * Six figures, no zone designator. Absent is not an error — a marker with
   * no grid is simply not a grid source.
   */
  readonly grid?: string;
  /**
   * Mandatory whenever `grid` is present. Only "confirmed" may act as
   * Source A; anything else is displayed and reported, never used to resolve
   * a blank, and never silently promoted.
   */
  readonly gridState?: TraceGridState;
  readonly id: string;
  readonly label: string;
  /**
   * What the grid refers to. Checked for shape here and for membership where
   * it is used: an unrecognised term matches no blank, so it resolves
   * nothing and is reported rather than refused.
   */
  readonly referent?: string;
  /**
   * The point's place in the trace's own ordering, where the trace supplies
   * one. Added by the ruling of 18 September 2026 for one constraint: the
   * first report line of a trace is the LD and the last is the LOE, and
   * nothing else in the file can order them.
   *
   * Absent means the constraint stands down rather than guessing. A sidecar
   * that leaves it out is not wrong.
   */
  readonly sequence?: number;
  readonly type: string;
  readonly x: number;
  readonly y: number;
}

export type TraceMarker = TraceFeature;

export interface TracePhase {
  readonly bindItemId: string;
  readonly detail: string;
  readonly id: string;
  readonly label: string;
  readonly markers: readonly string[];
  readonly path: readonly TracePoint[];
}

export interface StoredRecordDataMap {
  readonly calibration: CalibrationData;
  readonly "cue-positions": FileValuesData;
  readonly event: EventData;
  readonly "feature-flags": FeatureFlagsData;
  readonly fills: FileValuesData;
  readonly "lexicon-choices": FileValuesData;
  readonly notepad: NotepadData;
  readonly questions: QuestionsData;
  readonly settings: SettingsData;
}

export type StoredRecordData = StoredRecordDataMap[StoredRecordType];

export interface TraceSidecar {
  readonly schemaVersion: typeof TRACE_SIDECAR_SCHEMA_VERSION;
  readonly declinationMils: number;
  readonly features: readonly TraceFeature[];
  readonly image: string;
  readonly markers: readonly TraceMarker[];
  readonly phases: readonly TracePhase[];
}

export interface GoldenOutput {
  readonly schemaVersion: 1;
  readonly [key: string]: JsonValue;
}

export type SuiteStatus = "failed" | "passed" | "skipped";

export interface SuiteResult {
  readonly durationMs: number;
  readonly id: string;
  readonly status: SuiteStatus;
}

export interface TestRunResult {
  readonly schemaVersion: 1;
  readonly finishedAt: string;
  readonly overallStatus: "failed" | "passed";
  readonly suites: readonly SuiteResult[];
}

export const APPLICATION_SCHEMA_CATALOG = Object.freeze({
  schemaVersion: APPLICATION_SCHEMA_VERSION,
  fileSchemas: ["trace-sidecar", "golden-output", "test-results"] as const,
  storedRecordTypes: STORED_RECORD_TYPES,
});

function requireVersionOne(
  candidate: Record<string, unknown>,
  source: string,
): ValidationResult<1> {
  if (candidate.schemaVersion !== APPLICATION_SCHEMA_VERSION) {
    return validationFailure(
      source,
      "$.schemaVersion",
      "expected application schemaVersion 1",
    );
  }
  return validationSuccess(APPLICATION_SCHEMA_VERSION);
}

function requireString(
  candidate: Record<string, unknown>,
  key: string,
  source: string,
): ValidationResult<string> {
  const value = candidate[key];
  return typeof value === "string"
    ? validationSuccess(value)
    : validationFailure(source, `$.${key}`, "expected a string");
}

function requireStringMap(
  candidate: Record<string, unknown>,
  key: string,
  source: string,
): ValidationResult<Record<string, string>> {
  const value = candidate[key];
  return isStringRecord(value)
    ? validationSuccess(value)
    : validationFailure(
        source,
        `$.${key}`,
        "expected an object containing only string values",
      );
}

function requireNonEmptyStringAt(
  candidate: Record<string, unknown>,
  key: string,
  path: string,
  source: string,
): ValidationResult<string> {
  const value = candidate[key];
  return typeof value === "string" && value.trim() !== ""
    ? validationSuccess(value)
    : validationFailure(
        source,
        `${path}.${key}`,
        "expected a non-empty string",
      );
}

function validateNormalizedCoordinate(
  value: unknown,
  path: string,
  source: string,
): ValidationResult<number> {
  return typeof value === "number" &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= 1
    ? validationSuccess(value)
    : validationFailure(
        source,
        path,
        "expected a finite coordinate from 0 to 1",
      );
}

const SIX_FIGURE_GRID = /^\d{6}$/;

function isTraceGridState(value: unknown): value is TraceGridState {
  return TRACE_GRID_STATES.some((state) => state === value);
}

/**
 * The grid provenance trio of Ruling 25, with the meanings Ruling 30 gave
 * them. All three are optional; what is not optional is that they agree with
 * one another.
 *
 * `unread` means no value is claimed, so it may stand without a grid.
 * `confirmed` and `low-confidence` describe a value, so they may not.
 *
 * A four-figure value is refused rather than padded or truncated. Every grid
 * is six figures by the ruling of 12 September 2026, and a four-figure value
 * in a sidecar is an authoring slip that would otherwise resolve a blank to
 * the wrong kilometre square. The message names the marker so the file can be
 * corrected without hunting.
 */
function validateGridProvenance(
  value: Record<string, unknown>,
  path: string,
  source: string,
  markerId: string,
): ValidationResult<true> {
  const hasGrid = value.grid !== undefined;
  const hasState = value.gridState !== undefined;

  if (hasGrid) {
    if (typeof value.grid !== "string" || !SIX_FIGURE_GRID.test(value.grid)) {
      return validationFailure(
        source,
        `${path}.grid`,
        `marker "${markerId}": expected a six-figure grid, digits only, no zone designator`,
      );
    }
    if (!hasState) {
      return validationFailure(
        source,
        `${path}.gridState`,
        `marker "${markerId}": a grid must state how well it is known`,
      );
    }
  } else if (hasState && value.gridState !== "unread") {
    // Ruling 30: "unread" *is* the absence of a value, so it stands alone.
    // The other two describe a value, and a value has to be there to
    // describe. The first version of this check refused all three, and the
    // first real sidecar caught it on its first marker.
    return validationFailure(
      source,
      `${path}.gridState`,
      `marker "${markerId}": ${String(value.gridState)} describes a grid, and there is no grid here`,
    );
  }

  if (hasState && !isTraceGridState(value.gridState)) {
    return validationFailure(
      source,
      `${path}.gridState`,
      `marker "${markerId}": expected ${TRACE_GRID_STATES.join(", ")}`,
    );
  }

  if (value.referent !== undefined) {
    const referent = requireNonEmptyStringAt(value, "referent", path, source);
    if (!referent.ok) return referent;
  }

  if (value.assignedTo !== undefined) {
    const assigned = requireNonEmptyStringAt(value, "assignedTo", path, source);
    if (!assigned.ok) return assigned;
  }

  if (
    value.sequence !== undefined &&
    (typeof value.sequence !== "number" || !Number.isInteger(value.sequence))
  ) {
    return validationFailure(
      source,
      `${path}.sequence`,
      `marker "${markerId}": expected a whole number, or nothing at all`,
    );
  }

  return validationSuccess(true);
}

function validateTracePointEntry(
  value: unknown,
  path: string,
  source: string,
): ValidationResult<TraceFeature> {
  if (!isRecord(value)) {
    return validationFailure(source, path, "expected a JSON object");
  }
  for (const key of ["id", "label", "type"] as const) {
    const field = requireNonEmptyStringAt(value, key, path, source);
    if (!field.ok) return field;
  }
  const x = validateNormalizedCoordinate(value.x, `${path}.x`, source);
  if (!x.ok) return x;
  const y = validateNormalizedCoordinate(value.y, `${path}.y`, source);
  if (!y.ok) return y;
  const provenance = validateGridProvenance(
    value,
    path,
    source,
    String(value.id),
  );
  return provenance.ok
    ? validationSuccess(value as unknown as TraceFeature)
    : provenance;
}

function validateTracePathPoint(
  value: unknown,
  path: string,
  source: string,
): ValidationResult<TracePoint> {
  if (!Array.isArray(value) || value.length !== 2) {
    return validationFailure(source, path, "expected a two-coordinate point");
  }
  const x = validateNormalizedCoordinate(value[0], `${path}[0]`, source);
  if (!x.ok) return x;
  const y = validateNormalizedCoordinate(value[1], `${path}[1]`, source);
  return y.ok ? validationSuccess([x.value, y.value] as const) : y;
}

function validateTracePhase(
  value: unknown,
  path: string,
  source: string,
  markerIds: ReadonlySet<string>,
): ValidationResult<TracePhase> {
  if (!isRecord(value)) {
    return validationFailure(source, path, "expected a JSON object");
  }
  for (const key of ["id", "label", "bindItemId", "detail"] as const) {
    const field = requireNonEmptyStringAt(value, key, path, source);
    if (!field.ok) return field;
  }
  if (!Array.isArray(value.markers)) {
    return validationFailure(source, `${path}.markers`, "expected an array");
  }
  for (const [index, markerId] of value.markers.entries()) {
    const markerPath = `${path}.markers[${String(index)}]`;
    if (typeof markerId !== "string" || markerId.trim() === "") {
      return validationFailure(
        source,
        markerPath,
        "expected a non-empty marker id",
      );
    }
    if (!markerIds.has(markerId)) {
      return validationFailure(
        source,
        markerPath,
        "references an unknown marker id",
      );
    }
  }
  if (!Array.isArray(value.path)) {
    return validationFailure(source, `${path}.path`, "expected an array");
  }
  for (const [index, point] of value.path.entries()) {
    const result = validateTracePathPoint(
      point,
      `${path}.path[${String(index)}]`,
      source,
    );
    if (!result.ok) return result;
  }
  return validationSuccess(value as unknown as TracePhase);
}

export function validateTraceSidecar(
  value: unknown,
  source: string,
): ValidationResult<TraceSidecar> {
  if (!isRecord(value)) {
    return validationFailure(source, "$", "expected a JSON object");
  }
  if (value.schemaVersion !== TRACE_SIDECAR_SCHEMA_VERSION) {
    return validationFailure(
      source,
      "$.schemaVersion",
      `expected trace sidecar schemaVersion ${String(TRACE_SIDECAR_SCHEMA_VERSION)}`,
    );
  }

  const image = requireString(value, "image", source);
  if (!image.ok) return image;
  if (image.value.trim() === "") {
    return validationFailure(source, "$.image", "expected a non-empty string");
  }
  if (
    typeof value.declinationMils !== "number" ||
    !Number.isFinite(value.declinationMils)
  ) {
    return validationFailure(
      source,
      "$.declinationMils",
      "expected a finite number",
    );
  }
  const features = value.features;
  const markers = value.markers;
  const phases = value.phases;
  if (!Array.isArray(features))
    return validationFailure(source, "$.features", "expected an array");
  if (!Array.isArray(markers))
    return validationFailure(source, "$.markers", "expected an array");
  if (!Array.isArray(phases))
    return validationFailure(source, "$.phases", "expected an array");

  const ids = new Set<string>();
  const markerIds = new Set<string>();
  for (const [key, entries] of [
    ["features", features],
    ["markers", markers],
  ] as const) {
    for (const [index, entry] of entries.entries()) {
      const path = `$.${key}[${String(index)}]`;
      const point = validateTracePointEntry(entry, path, source);
      if (!point.ok) return point;
      if (ids.has(point.value.id)) {
        return validationFailure(source, `${path}.id`, "expected a unique id");
      }
      ids.add(point.value.id);
      if (key === "markers") markerIds.add(point.value.id);
    }
  }
  for (const [index, entry] of phases.entries()) {
    const path = `$.phases[${String(index)}]`;
    const phase = validateTracePhase(entry, path, source, markerIds);
    if (!phase.ok) return phase;
    if (ids.has(phase.value.id)) {
      return validationFailure(source, `${path}.id`, "expected a unique id");
    }
    ids.add(phase.value.id);
  }
  return validationSuccess(value as unknown as TraceSidecar);
}

export function validateGoldenOutput(
  value: unknown,
  source: string,
): ValidationResult<GoldenOutput> {
  if (!isRecord(value)) {
    return validationFailure(source, "$", "expected a JSON object");
  }
  const version = requireVersionOne(value, source);
  return version.ok ? validationSuccess(value as GoldenOutput) : version;
}

function isSuiteStatus(value: unknown): value is SuiteStatus {
  return value === "failed" || value === "passed" || value === "skipped";
}

export function validateTestRunResult(
  value: unknown,
  source: string,
): ValidationResult<TestRunResult> {
  if (!isRecord(value)) {
    return validationFailure(source, "$", "expected a JSON object");
  }
  const version = requireVersionOne(value, source);
  if (!version.ok) return version;
  if (typeof value.finishedAt !== "string") {
    return validationFailure(source, "$.finishedAt", "expected a string");
  }
  if (value.overallStatus !== "failed" && value.overallStatus !== "passed") {
    return validationFailure(
      source,
      "$.overallStatus",
      'expected "failed" or "passed"',
    );
  }
  if (!Array.isArray(value.suites)) {
    return validationFailure(source, "$.suites", "expected an array");
  }
  for (const [index, suite] of value.suites.entries()) {
    const path = `$.suites[${String(index)}]`;
    if (!isRecord(suite)) {
      return validationFailure(source, path, "expected a JSON object");
    }
    if (typeof suite.id !== "string") {
      return validationFailure(source, `${path}.id`, "expected a string");
    }
    if (
      typeof suite.durationMs !== "number" ||
      !Number.isFinite(suite.durationMs) ||
      suite.durationMs < 0
    ) {
      return validationFailure(
        source,
        `${path}.durationMs`,
        "expected a non-negative finite number",
      );
    }
    if (!isSuiteStatus(suite.status)) {
      return validationFailure(
        source,
        `${path}.status`,
        'expected "failed", "passed", or "skipped"',
      );
    }
  }
  return validationSuccess(value as unknown as TestRunResult);
}

function validateEventData(
  data: Record<string, unknown>,
  source: string,
): ValidationResult<EventData> {
  for (const key of ["module", "action", "message"] as const) {
    const result = requireString(data, key, source);
    if (!result.ok) return result;
  }
  if (data.stack !== null && typeof data.stack !== "string") {
    return validationFailure(source, "$.stack", "expected a string or null");
  }
  return validationSuccess(data as unknown as EventData);
}

function validateSettingsData(
  data: Record<string, unknown>,
  source: string,
): ValidationResult<SettingsData> {
  return isJsonObject(data.values)
    ? validationSuccess(data as unknown as SettingsData)
    : validationFailure(
        source,
        "$.values",
        "expected an object containing only finite JSON values",
      );
}

function validateCalibrationData(
  data: Record<string, unknown>,
  source: string,
): ValidationResult<CalibrationData> {
  const voiceId = requireString(data, "voiceId", source);
  if (!voiceId.ok) return voiceId;
  if (
    typeof data.wordsPerMinute !== "number" ||
    !Number.isFinite(data.wordsPerMinute) ||
    data.wordsPerMinute <= 0
  ) {
    return validationFailure(
      source,
      "$.wordsPerMinute",
      "expected a positive finite number",
    );
  }
  return validationSuccess(data as unknown as CalibrationData);
}

function validateFileValuesData(
  data: Record<string, unknown>,
  source: string,
): ValidationResult<FileValuesData> {
  const fileHash = requireString(data, "fileHash", source);
  if (!fileHash.ok) return fileHash;
  const values = requireStringMap(data, "values", source);
  return values.ok
    ? validationSuccess(data as unknown as FileValuesData)
    : values;
}

function validateNotepadData(
  data: Record<string, unknown>,
  source: string,
): ValidationResult<NotepadData> {
  const fileHash = requireString(data, "fileHash", source);
  if (!fileHash.ok) return fileHash;
  const text = requireString(data, "text", source);
  return text.ok ? validationSuccess(data as unknown as NotepadData) : text;
}

function validateFeatureFlagsData(
  data: Record<string, unknown>,
  source: string,
): ValidationResult<FeatureFlagsData> {
  if (!isRecord(data.values)) {
    return validationFailure(source, "$.values", "expected a JSON object");
  }
  for (const key of FEATURE_FLAG_KEYS) {
    if (typeof data.values[key] !== "boolean") {
      return validationFailure(source, `$.values.${key}`, "expected a boolean");
    }
  }
  return validationSuccess(data as unknown as FeatureFlagsData);
}

function validateQuestionsData(
  candidate: Record<string, unknown>,
  source: string,
): ValidationResult<QuestionsData> {
  const fileHash = requireString(candidate, "fileHash", source);
  if (!fileHash.ok) return fileHash;
  const entries = candidate.revealedWithoutAnswering;
  if (!Array.isArray(entries)) {
    return validationFailure(
      source,
      "$.revealedWithoutAnswering",
      "expected an array",
    );
  }
  for (const [index, entry] of entries.entries()) {
    const path = `$.revealedWithoutAnswering[${String(index)}]`;
    if (!isRecord(entry)) {
      return validationFailure(source, path, "expected a JSON object");
    }
    for (const key of ["questionId", "nodeId"] as const) {
      const field = requireNonEmptyStringAt(entry, key, path, source);
      if (!field.ok) return field;
    }
  }
  return validationSuccess(candidate as unknown as QuestionsData);
}

export function validateStoredRecordData<K extends StoredRecordType>(
  recordType: K,
  value: unknown,
  source: string,
): ValidationResult<StoredRecordDataMap[K]> {
  if (!isRecord(value)) {
    return validationFailure(source, "$", "expected a JSON object");
  }

  let result: ValidationResult<StoredRecordData>;
  switch (recordType) {
    case "event":
      result = validateEventData(value, source);
      break;
    case "settings":
      result = validateSettingsData(value, source);
      break;
    case "calibration":
      result = validateCalibrationData(value, source);
      break;
    case "fills":
    case "cue-positions":
    case "lexicon-choices":
      result = validateFileValuesData(value, source);
      break;
    case "notepad":
      result = validateNotepadData(value, source);
      break;
    case "questions":
      result = validateQuestionsData(value, source);
      break;
    case "feature-flags":
      result = validateFeatureFlagsData(value, source);
      break;
  }
  return result as ValidationResult<StoredRecordDataMap[K]>;
}

export function isStoredRecordType(value: unknown): value is StoredRecordType {
  return (
    typeof value === "string" &&
    STORED_RECORD_TYPES.includes(value as StoredRecordType)
  );
}
